import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/joy/Box';
import type { InboxItem } from '../../types/inbox';
import UnifiedInboxHeader from './UnifiedInboxHeader';
import UnifiedInboxList from './UnifiedInboxList';
import { filterInboxItems } from './inboxItemFilters';
import api from '../../lib/api';

interface InboxLayoutProps {
  items: InboxItem[];
  loading: boolean;
  loadingMore?: boolean;
  /** Unfiltered server total. The header shows the filtered count instead. */
  totalCount?: number;
  hasMore?: boolean;
  onRefresh: () => void;
  onLoadMore?: () => void;
  selectedItems: Set<number>;
  onSelectAll: (checked: boolean) => void;
  onItemSelect: (itemId: number, selected: boolean) => void;
  onBulkDone: () => void;
  onBulkSnooze: () => void;
  onItemDone: (itemId: number) => void;
  onItemSnooze: (itemId: number) => void;
  onItemEmail: (item: InboxItem) => void;
  onItemCall: (item: InboxItem) => void;
  onItemText: (item: InboxItem) => void;
  onItemReply: (itemId: number, content: string, type: 'email' | 'sms') => void;
}

const InboxLayout: React.FC<InboxLayoutProps> = ({
  items,
  loading,
  loadingMore = false,
  hasMore = false,
  onRefresh,
  onLoadMore,
  selectedItems,
  onSelectAll,
  onItemSelect,
  onBulkDone,
  onBulkSnooze,
  onItemDone,
  onItemSnooze,
  onItemEmail,
  onItemCall,
  onItemText,
  onItemReply,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [contentFilter, setContentFilter] = useState<'all' | 'email' | 'call' | 'sms' | 'task'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'oldest' | 'newest' | 'priority'>('newest');
  const [assignees, setAssignees] = useState<Array<{ id: string; name: string }>>([]);

  // The assignee filter stays null — "All Users" — until the user picks
  // someone. It used to be set to the current user on mount, by a
  // loadCurrentUser() whose only other effect was populating a `currentUser`
  // state nothing read.
  //
  // That silently hid every unassigned item, which is the shared pool anyone
  // in the org can see and claim. And it was invisible: the Select's options
  // are "All Users" plus whatever loadAssignees() returns, and that calls
  // /api/users/, which is admin-only — so for an ordinary rep the list is
  // empty, the selected id matches no Option, and Joy UI falls back to
  // rendering the placeholder. The control read "All Users" while filtering
  // to you, and the only way out was to open it and pick the one option it
  // already claimed to be on.
  useEffect(() => {
    loadAssignees();
  }, []);

  const loadAssignees = async () => {
    try {
      const response = await api.get<any>('/api/users/');
      const users = Array.isArray(response.data) ? response.data : (response.data as any).results || [];
      setAssignees(users.map((user: any) => ({
        id: user.id.toString(),
        name: user.name || `${user.first_name} ${user.last_name}`.trim() || user.email,
      })));
    } catch (error) {
      // Expected for non-admins: /api/users/ is admin-only, so the dropdown
      // offers "All Users" alone and the list is simply unfiltered.
      console.error('Failed to load assignees:', error);
    }
  };

  const handleItemExpand = (itemId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      // Only one item expanded at a time (optional)
      // newExpanded.clear();
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  // Texting happens in the expanded row (thread + composer), so Text opens the
  // row rather than toggling it shut when it already happens to be open.
  const handleItemText = (item: InboxItem) => {
    setExpandedItems((prev) => new Set(prev).add(item.id));
    onItemText(item);
  };

  // Count what's actually on screen, so "Inbox (n)" matches the visible rows
  // rather than the unfiltered server total.
  const visibleCount = useMemo(
    () => filterInboxItems(items, contentFilter, assigneeFilter).length,
    [items, contentFilter, assigneeFilter]
  );

  const isAllSelected = selectedItems.size === items.length && items.length > 0;
  const isPartiallySelected = selectedItems.size > 0 && selectedItems.size < items.length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Unified Header */}
      <UnifiedInboxHeader
        totalCount={visibleCount}
        selectedCount={selectedItems.size}
        isAllSelected={isAllSelected}
        isPartiallySelected={isPartiallySelected}
        contentFilter={contentFilter}
        assigneeFilter={assigneeFilter}
        sortOrder={sortOrder}
        assignees={assignees}
        onSelectAll={onSelectAll}
        onBulkDone={onBulkDone}
        onBulkSnooze={onBulkSnooze}
        onContentFilterChange={setContentFilter}
        onAssigneeFilterChange={setAssigneeFilter}
        onSortOrderChange={setSortOrder}
      />

      {/* Unified List */}
      <UnifiedInboxList
        items={items}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        selectedItems={selectedItems}
        expandedItems={expandedItems}
        contentFilter={contentFilter}
        assigneeFilter={assigneeFilter}
        sortOrder={sortOrder}
        onItemSelect={onItemSelect}
        onItemExpand={handleItemExpand}
        onItemDone={onItemDone}
        onItemSnooze={onItemSnooze}
        onItemEmail={onItemEmail}
        onItemCall={onItemCall}
        onItemText={handleItemText}
        onItemReply={onItemReply}
        onRefresh={onRefresh}
        onLoadMore={onLoadMore}
      />
    </Box>
  );
};

export default InboxLayout;